import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Calendar, BookOpen, MessageCircle, BarChart, 
  Settings, Globe, Shield, ChevronRight, Car, Droplet, 
  Smartphone, Clock, CreditCard, PieChart, Zap, Award,
  Check, Star, Send, Mail, ChevronLeft, ChevronDown,
  Bot, MessagesSquare, Layers, BrainCircuit, History
} from 'lucide-react';
import SectionTransitionEffect from './ui/SectionTransitionEffect';

const Features: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('client');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  const categories = [
    {
      id: 'client',
      name: 'Client Management',
      icon: <Users size={24} />,
      description: 'Everything you need to manage client relationships effectively'
    },
    {
      id: 'communication',
      name: 'Communication',
      icon: <MessageCircle size={24} />,
      description: 'Keep in touch with clients through automated messaging systems'
    },
    {
      id: 'growth',
      name: 'Business Growth',
      icon: <BarChart size={24} />,
      description: 'Tools to scale your detailing business to new heights'
    },
    {
      id: 'marketing',
      name: 'Marketing',
      icon: <Globe size={24} />,
      description: 'Attract new clients and retain existing ones'
    }
  ];

  const featuresByCategory = {
    client: [
      {
        icon: <Users className="text-accent" size={28} />,
        title: 'Client Relationship Management',
        description: 'Build stronger relationships with easy access to client preferences, vehicle information, and service history.',
        demoIcon: <Car className="text-accent" />
      },
      {
        icon: <Calendar className="text-accent" size={28} />,
        title: 'Smart Booking System',
        description: 'Let clients book appointments online and automatically sync with your business calendar.',
        demoIcon: <Clock className="text-accent" />
      },
      {
        icon: <History className="text-accent" size={28} />,
        title: 'Complete Communication History',
        description: 'Every client interaction is automatically recorded and retained, ensuring you never miss important details.',
        demoIcon: <MessageCircle className="text-accent" />
      }
    ],
    communication: [
      {
        icon: <MessagesSquare className="text-accent" size={28} />,
        title: 'Mass Text Messaging',
        description: 'Send personalized text messages to your entire client base or targeted segments with just a few clicks.',
        demoIcon: <Smartphone className="text-accent" />
      },
      {
        icon: <Mail className="text-accent" size={28} />,
        title: 'Mass Email Campaigns',
        description: 'Create and send beautiful email campaigns to promote your services and stay top of mind with clients.',
        demoIcon: <Send className="text-accent" />
      },
      {
        icon: <Layers className="text-accent" size={28} />,
        title: 'Automated Email Drip',
        description: 'Set up automated email sequences that nurture leads and maintain client relationships on autopilot.',
        demoIcon: <Clock className="text-accent" />
      }
    ],
    growth: [
      {
        icon: <PieChart className="text-accent" size={28} />,
        title: 'Business Analytics (in development)',
        description: 'Comprehensive reporting on revenue, popular services, customer retention, and growth opportunities.',
        demoIcon: <BarChart className="text-accent" />
      },
      {
        icon: <Zap className="text-accent" size={28} />,
        title: 'Performance Insights (in development)',
        description: 'AI-powered business intelligence that identifies trends and suggests optimizations.',
        demoIcon: <Zap className="text-accent" />
      },
      {
        icon: <Award className="text-accent" size={28} />,
        title: 'Expert Coaching',
        description: 'Access to industry mentorship and personalized guidance to scale your detailing business.',
        demoIcon: <Users className="text-accent" />
      }
    ],
    marketing: [
      {
        icon: <Bot className="text-accent" size={28} />,
        title: 'AI Industry Sales Rep CHiP',
        description: 'Your AI assistant that knows everything about detailing and can answer questions or book consulting calls.',
        demoIcon: <BrainCircuit className="text-accent" />
      },
      {
        icon: <Globe className="text-accent" size={28} />,
        title: 'Website Live AI with Handoff',
        description: 'AI-powered live chat on your website that can seamlessly transition to a human agent when needed.',
        demoIcon: <MessagesSquare className="text-accent" />
      },
      {
        icon: <MessageCircle className="text-accent" size={28} />,
        title: 'Review Collection',
        description: 'Automatically request and showcase 5-star reviews from your satisfied customers.',
        demoIcon: <Star className="text-accent" />
      }
    ]
  };

  // Function to navigate between features on mobile
  const navigateFeature = (direction: 'prev' | 'next') => {
    const currentIndex = categories.findIndex(c => c.id === activeCategory);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % categories.length;
    } else {
      newIndex = (currentIndex - 1 + categories.length) % categories.length;
    }
    
    setActiveCategory(categories[newIndex].id);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1.0] }
    }
  };

  const cardVariants = {
    initial: { y: 50, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -50, opacity: 0 },
    hover: { y: -10, transition: { duration: 0.2 } }
  };

  const currentCategory = categories.find(c => c.id === activeCategory);

  return (
    <section id="features" className="py-20 relative overflow-hidden">
      {/* Background gradient */}
      <motion.div 
        className="absolute rounded-full opacity-60 blur-3xl -z-10 w-[600px] h-[600px] bottom-0 right-0"
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.6, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(204, 255, 0, 0.3), rgba(204, 255, 0, 0) 70%)'
        }}
      />
      
      <div className="container mx-auto px-4">
        <SectionTransitionEffect>
          <div 
            className="text-center mb-12 max-w-4xl mx-auto"
          >
            <div 
              className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
            >
              Powerful Features
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-6">Everything You Need to Scale Your Detailing Business</h2>
            <p className="text-white/70 text-lg mb-8">Our all-in-one platform provides the tools, automation, and insights to deliver exceptional detailing services while reducing your administrative overhead.</p>
            
            {/* Mobile Dropdown Selector */}
            <div className="md:hidden mb-4">
              <motion.button
                className="w-full glassmorphic rounded-lg p-3 flex items-center justify-between"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-accent">{currentCategory?.icon}</span>
                  <span className="font-semibold">{currentCategory?.name}</span>
                </div>
                <ChevronDown 
                  className={`transition-transform duration-300 ${showMobileMenu ? 'rotate-180' : ''}`} 
                  size={20} 
                />
              </motion.button>
              
              {showMobileMenu && (
                <motion.div 
                  className="glassmorphic rounded-lg mt-2 p-2 absolute z-20 w-[calc(100%-2rem)] left-4"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  {categories.map((category) => (
                    <motion.button
                      key={category.id}
                      className={`w-full text-left p-3 rounded-lg flex items-center space-x-3 my-1 ${
                        activeCategory === category.id
                          ? 'bg-accent text-background'
                          : 'hover:bg-white/10'
                      }`}
                      onClick={() => {
                        setActiveCategory(category.id);
                        setShowMobileMenu(false);
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span>{category.icon}</span>
                      <span className="font-semibold">{category.name}</span>
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </div>
            
            {/* Desktop Category Selector - Hidden on mobile */}
            <motion.div 
              className="hidden md:flex justify-center gap-4 mb-8 lg:gap-6 overflow-x-auto no-scrollbar"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  className={`px-4 py-2 md:px-6 rounded-lg flex items-center space-x-2 transition-colors flex-shrink-0 ${
                    activeCategory === category.id
                      ? 'bg-accent text-background'
                      : 'glassmorphic hover:bg-white/10'
                  }`}
                  onClick={() => setActiveCategory(category.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="flex items-center justify-center">{category.icon}</span>
                  <span className="font-semibold text-sm md:text-base whitespace-nowrap">{category.name}</span>
                </motion.button>
              ))}
            </motion.div>
          </div>
        </SectionTransitionEffect>
        
        {/* Mobile Swipeable View with Navigation Buttons */}
        <div className="md:hidden relative mb-6">
          <div className="flex justify-between items-center mb-3">
            <motion.button
              className="w-10 h-10 rounded-full glassmorphic flex items-center justify-center"
              onClick={() => navigateFeature('prev')}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft size={20} />
            </motion.button>
            
            <div className="text-center">
              <span className="font-semibold">{currentCategory?.name}</span>
            </div>
            
            <motion.button
              className="w-10 h-10 rounded-full glassmorphic flex items-center justify-center"
              onClick={() => navigateFeature('next')}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRight size={20} />
            </motion.button>
          </div>
          
          <AnimatePresence mode="wait">
            {featuresByCategory[activeCategory].map((feature, index) => (
              <motion.div
                key={`mobile-${activeCategory}-${index}`}
                className="glassmorphic rounded-xl p-4 relative overflow-hidden min-h-[140px] mb-3 hover:border-accent/30"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    {feature.icon}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-bold mb-1">{feature.title}</h3>
                    <p className="text-white/70 text-sm">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          
          <div className="flex justify-center space-x-1 mt-4">
            {categories.map((category, index) => (
              <button
                key={`dot-${index}`}
                className={`h-2 rounded-full transition-all ${
                  activeCategory === category.id ? 'w-6 bg-accent' : 'w-2 bg-white/30'
                }`}
                onClick={() => setActiveCategory(category.id)}
                aria-label={`Switch to ${category.name}`}
              />
            ))}
          </div>
        </div>
        
        {/* Desktop Feature Cards - Hidden on mobile */}
        <div className="hidden md:block relative min-h-[500px] mb-4 perspective-1000">
          <AnimatePresence mode="wait">
            <motion.div 
              key={activeCategory}
              initial="initial"
              animate="animate"
              exit="exit"
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {featuresByCategory[activeCategory].map((feature, index) => (
                <motion.div
                  key={`${activeCategory}-${index}`}
                  className="glassmorphic rounded-xl p-6 relative overflow-hidden group hover:border-accent/30 hover:shadow-glow-sm"
                  variants={cardVariants}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover="hover"
                >
                  {/* Background animation on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0"></div>
                  
                  {/* Card content */}
                  <div className="relative z-10">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-14 h-14 rounded-lg bg-accent/10 flex items-center justify-center">
                        {feature.icon}
                      </div>
                      
                      <motion.div 
                        className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center"
                        whileHover={{ rotate: 15, scale: 1.1 }}
                      >
                        {feature.demoIcon}
                      </motion.div>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                    <p className="text-white/70 mb-6">{feature.description}</p>
                    
                    <motion.div 
                      className="flex items-center text-accent font-semibold text-sm space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      whileHover={{ x: 5 }}
                    >
                      <span>Learn more</span>
                      <ChevronRight size={16} />
                    </motion.div>
                  </div>
                  
                  {/* Corner decoration */}
                  <div className="absolute -bottom-10 -right-10 w-40 h-40 opacity-20 bg-gradient-to-tr from-accent to-transparent rounded-full z-0 transform scale-0 group-hover:scale-100 transition-transform duration-500"></div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
        
        {/* Category Description */}
        <motion.div 
          className="hidden md:block text-center max-w-xl mx-auto mb-8"
          key={activeCategory + "-desc"}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {categories.find(c => c.id === activeCategory)?.description}
        </motion.div>
        
        {/* Interactive Feature Highlight - More condensed on mobile */}
        <SectionTransitionEffect delay={0.3}>
          <div 
            className="glassmorphic rounded-2xl p-6 md:p-12 overflow-hidden relative border border-white/20 shadow-glass max-w-5xl mx-auto mt-8 md:mt-0"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-accent/10 to-transparent opacity-70"></div>
            
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
              <div className="w-full md:w-1/2">
                <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">DetailerOS + GRIT SUITE Integration</h3>
                <p className="text-white/80 text-sm md:text-base mb-4 md:mb-6">Experience the power of two industry-leading platforms working together seamlessly. Manage your detailing business and client relationships in one unified system.</p>
                
                <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                  {['Unified client database', 'Seamless data synchronization', 'Powerful automation workflows', 'Combined reporting insights'].map((item, i) => (
                    <motion.li 
                      key={i} 
                      className="flex items-center"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + (i * 0.1), duration: 0.5 }}
                    >
                      <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-accent flex items-center justify-center text-background mr-2 md:mr-3 flex-shrink-0">
                        <Check size={12} className="md:hidden" />
                        <Check size={14} className="hidden md:block" />
                      </div>
                      <span className="text-sm md:text-base">{item}</span>
                    </motion.li>
                  ))}
                </ul>
                
                <motion.button 
                  className="btn btn-primary text-sm md:text-base py-2 md:py-3"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  See Integration Details
                </motion.button>
              </div>
              
              {/* Interactive visualization - simplified on mobile */}
              <motion.div 
                className="w-full md:w-1/2 perspective-800"
                initial={{ rotateY: 15 }}
                whileHover={{ rotateY: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative w-full aspect-video bg-background/40 rounded-xl overflow-hidden border border-white/10 transform rotate-y-15 shadow-glow-sm">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-transparent to-transparent opacity-70"></div>
                  <div className="absolute inset-0 p-3 md:p-4 flex flex-col justify-between">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-accent flex items-center justify-center text-background font-bold">D</div>
                        <span className="font-semibold text-sm md:text-base text-white">DetailerOS</span>
                      </div>
                      <div className="flex space-x-1 md:space-x-2">
                        <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-red-500"></div>
                        <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-yellow-500"></div>
                        <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-500"></div>
                      </div>
                    </div>
                    
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <motion.div 
                          className="inline-block"
                          animate={{ 
                            rotateY: [0, 360],
                          }}
                          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                        >
                          <Car size={40} className="md:hidden text-accent mb-2" />
                          <Car size={64} className="hidden md:block text-accent mb-4" />
                        </motion.div>
                        <div className="text-lg md:text-2xl font-bold">DetailerOS + GRIT SUITE</div>
                        <div className="text-xs md:text-base text-white/70">Powerful Integration</div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center bg-white/10 p-1.5 md:p-2 rounded-lg">
                      <div className="w-16 md:w-24 h-1.5 md:h-2 bg-accent/50 rounded-full"></div>
                      <div className="text-[10px] md:text-xs text-white/70">Syncing data...</div>
                      <div className="w-4 h-4 md:w-6 md:h-6 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </SectionTransitionEffect>
      </div>
    </section>
  );
};

export default Features;